// Transient confirmation of something that has already happened.
//
// WHERE THIS IS THE RIGHT ANSWER, AND WHERE IT IS NOT
//
// An inline message next to the work is better wherever one is possible: it is where the person is
// already looking. This exists for the case where that is impossible - the screen the message would
// have appeared on has been replaced, because the action that succeeded also navigated. Saving and
// closing a deal is exactly that, and is the only caller today.
//
// It is not for errors. A refusal is something the person has to act on, and it belongs beside the
// control that was refused, where it stays until it is dealt with.
//
// WHY THE INTERFACE IS THIS NARROW
//
// A message and, optionally, one thing to do about it. No duration, no position, no variant: each
// of those is a design decision that must have one answer across the application, and a call site
// that can pick its own is a call site that will. They are set once, on <Toaster> in App.
//
// SONNER IS NAMED HERE AND NOWHERE ELSE
//
// The same seam as AIService, for the same reason - a dependency one file imports can be replaced,
// and one that fifteen files import cannot. What the library is buying is the part that looks
// trivial and is not: a live region that announces without stealing focus, a timer that pauses on
// hover and on window blur, swipe-to-dismiss on a touch screen, and a banner that renders correctly
// when whatever raised it has already unmounted - which is the normal case here, because the form
// is gone by the time it appears.

import { Toaster, toast } from 'sonner';

// Long enough to read a deal identifier and reach for the action, short enough that it is gone
// before it becomes furniture.
const DURATION_MS = 5000;

// Bottom-centred at every width: where both Material and iOS put a transient confirmation, so it is
// where a thumb expects to dismiss one, and the edge of the screen furthest from what is being read.
// The inset keeps it above the home indicator on a phone that has one.
const OFFSET = 'calc(1rem + env(safe-area-inset-bottom))';

/**
 * Confirm something that has already succeeded.
 *
 * @param {string} message What happened, naming the thing it happened to.
 * @param {{ action?: { label: string, onClick: () => void } }} [options] One way to act on it.
 */
export function notify(message, { action } = {}) {
  toast.success(message, action ? { action } : undefined);
}

/**
 * Where the banners render. Mounted once, in the shell.
 *
 * In the shell rather than in a route because the toast is raised immediately before navigating
 * away: anything mounted under the route that raised it is gone before the banner appears.
 */
export function ToastHost() {
  return (
    <Toaster
      position="bottom-center"
      offset={OFFSET}
      mobileOffset={OFFSET}
      duration={DURATION_MS}
      closeButton
      // Colours come from styles.css, through sonner's own custom properties. Nothing here picks a
      // colour, in or out of the palette.
      toastOptions={{ className: 'trex-toast' }}
    />
  );
}
